import React, { useEffect, useState } from "react";
import { ChatContextModel, Chat, Message } from "../models/Chat";
import { LoadingState } from "../utils/enum";
import useWebSocket from "../hooks/useWebsocket";
import {
  COMPANY_ROLE,
  COOKIES_COMPANY_ID,
  WEBSOCKET_CLIENT_ACTIONS,
} from "../utils/constants";
import Cookies from "js-cookie";
import { fetchChats, sendMessageAPI } from "../services/chatAPI";
import { LoadData } from "../models";
import { fetchLoadDetails } from "../services/loadAPI";

export const ChatsContext = React.createContext<ChatContextModel>(
  {} as ChatContextModel
);

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [searchedChats, setSearchedChats] = useState<Chat[]>([]);
  const [searchedMessages, setSearchedMessages] = useState<Message[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string>();
  const [inputMessage, setInputMessage] = useState("");
  const [selectedChat, setSelectedChat] = useState<Chat>();
  const [loadingState, setLoadingState] = useState(LoadingState.Pending);
  const [participantDrawer, setParticipantDrawer] = useState(false);
  const [newMsgSent, setNewMsgSent] = useState(false);
  const [webSocket, setWebSocket] = useState<WebSocket>();
  const [currentChatLoad, setCurrentChatLoad] = useState<LoadData>();
  const [companyName, setCompanyName] = useState<string>("");
  const [companyID, setCompanyID] = useState<string | undefined>(
    Cookies.get(COOKIES_COMPANY_ID)
  );
  const [companyLoading, setCompanyLoading] = useState(LoadingState.None);
  const [tabNotificationCount, setTabNotificationCount] = useState(0);
  const { socket: ws, sendAction, handleReconnect } = useWebSocket();

  useEffect(() => {
    console.log("from chat context", { companyID, ws });
    if (!ws || !companyID) return;

    ws.onopen = () => {
      console.log("✅ WebSocket connected");
      setWebSocket(ws);
      if (loadingState === LoadingState.Success && chats.length > 0) {
        getNewestMessages();
      }
    };

    ws.onerror = (err) => {
      console.error("❌ WebSocket error", err);
    };

    ws.onclose = handleReconnect;
  }, [ws, companyID]);

  // Receives new message
  const handleIncomingNewMessage = (payload: any) => {
    let updatedChats = [...chats];
    const { message, chatID } = payload.data as {
      message: any;
      chatID: string;
    };
    const index = updatedChats.findIndex((chat) => chat.id === chatID);
    console.log("incoming message in chatContext", {
      data: payload.data,
      index,
      updatedChats,
    });
    if (index >= 0) {
      // update the read status of message if it's from self
      if (message.senderID === companyID) {
        const msgIndex = updatedChats[index].messages.findIndex(
          (m) => m.content.toLowerCase() === message.content.toLowerCase() // consider send temp id logic
        );
        console.log("message index", msgIndex);
        if (msgIndex >= 0) {
          updatedChats[index].messages[msgIndex] = {
            ...updatedChats[index].messages[msgIndex],
            status: "sent",
          };
        }
      } else {
        const data = {
          ...message,
          id: message.messageID,
          date: new Date(message.timestamp * 1000).toISOString(),
        };
        console.log("new message in chatContext", data);
        updatedChats[index].messages.push(data);
        updatedChats = sortChats(updatedChats);
      }
      setChats(updatedChats);
    }
  };

  const sortChats = (chatList: Chat[]) => {
    chatList.sort((a, b) => {
      const aTimestamp =
        a.messages.length > 0
          ? a.messages[a.messages.length - 1].timestamp
          : Number(a.createdAt);
      const bTimestamp =
        b.messages.length > 0
          ? b.messages[b.messages.length - 1].timestamp
          : Number(b.createdAt);
      return bTimestamp - aTimestamp; // Descending → latest first
    });
    return chatList;
  };

  // Receives list of all missed chats messages
  const handleIncomingNewChats = (payload: any) => {
    const updatedChats = [...chats];

    const newData = payload.data; // { chatID: [messages] }
    Object.keys(newData).forEach((chatID) => {
      const index = chats.findIndex((chat) => chat.id === chatID);
      if (index > 0) {
        updatedChats[index].messages.push(...newData[chatID]);
      }
    });
    setChats(updatedChats);
  };

  // Receives list of all chats
  const handleIncomingAllChats = (payload: any) => {
    const allChats = payload.data as any[]; // list of chats
    console.log("all chats", allChats);
    const data = mapResultToChats(allChats);
    setChats(data);
    setLoadingState(LoadingState.Success);
    return allChats;
  };

  // Get all messages for a chat
  const getActiveChats = async () => {
    // sendAction(WEBSOCKET_CLIENT_ACTIONS.fetchActiveChats, {});
    setLoadingState(LoadingState.Pending);
    const result = await fetchChats("active");
    const data = mapResultToChats(result);
    setChats(data);
    setLoadingState(LoadingState.Success);
  };

  // Fetch only new messages since last timestamp
  const getNewestMessages = () => {
    const payload = chats.map((item) => ({
      chatID: item.id,
      lastMessageTimestamp:
        item.messages.length > 0 ? item.messages[item.messages.length - 1] : 0,
    }));
    sendAction(WEBSOCKET_CLIENT_ACTIONS.fetchNewChatMessages, payload);
  };

  const mapResultToChats = (result: any[]) => {
    const data = result.map<Chat>((chat: any) => ({
      ...chat,
      id: chat.chatID,
      dropOffLocation: chat.dropOffLocation.name,
      pickupLocation: chat.pickupLocation.name,
      pickupAddress: chat.pickupLocation.address,
      dropOffAddress: chat.dropOffLocation.address,
      loadNumber: chat.loadNum,
      createdAt: new Date(chat.createdAt).toISOString(),
      messages: ((chat.messages as any[] | undefined) || []).map<Message>(
        (m) => ({
          ...m,
          id: m.messageID,
          date: new Date(m.timestamp * 1000).getTime(),
          status:
            m.senderID === companyID
              ? m.readers.length > 1 // currently we have only two participants in chat
                ? "read"
                : "sent"
              : undefined,
        })
      ),
    }));
    return data;
  };

  // read unread messages
  const readMessages = (
    data: {
      messageID: string;
      timestamp: number;
    }[]
  ) => {
    console.log("reading messages", data);
    sendAction(WEBSOCKET_CLIENT_ACTIONS.readMessage, {
      chatID: selectedChat?.id,
      messages: data,
    });
    const companyID = Cookies.get(COOKIES_COMPANY_ID);
    const updatedChats = [...chats];
    data.forEach((item) => {
      const chatIdx = updatedChats.findIndex((c) => c.id === selectedChat?.id);
      if (chatIdx >= 0) {
        updatedChats[chatIdx].messages = updatedChats[chatIdx].messages.map(
          (m) =>
            m.id === item.messageID
              ? { ...m, readers: [...m.readers, companyID!] }
              : m
        );
      }
    });
    console.log("after read", { updatedChats });
    setTimeout(() => {
      setChats(updatedChats);
    }, 3000);
  };

  // Send message to the chat group
  const sendMessage = async () => {
    if (!selectedChat || !inputMessage) return;

    const data = {
      chatID: selectedChat.id,
      senderID: companyID,
      senderRole: COMPANY_ROLE.carrier,
      content: inputMessage,
      date: new Date().toISOString(),
      timestamp: Math.floor(Date.now() / 1000),
    };

    const message: Message = {
      ...data,
      id: Date.now().toString(),
      readers: [companyID!],
      senderRole: "carrier",
      status: "pending",
      senderName: companyName,
    };

    let updatedChats = [...chats].map((chat) =>
      chat.id === selectedChat.id
        ? { ...chat, messages: [...chat.messages, message] }
        : chat
    );

    updatedChats = sortChats(updatedChats);
    setChats(updatedChats);
    // setNewMsgSent(!newMsgSent);

    const { sentMessage } = await sendMessageAPI(selectedChat.id, inputMessage);

    // Send websocket event
    sendAction(WEBSOCKET_CLIENT_ACTIONS.sendMessage, {
      message: sentMessage,
      chat: selectedChat,
    });

    // mark message as sent
    updatedChats = updateMessageStatus({
      chatData: updatedChats,
      chatID: selectedChat.id,
      messageID: message.id,
      newMessageID: sentMessage.messageID,
      status: "sent",
    });

    updatedChats = sortChats(updatedChats);
    setChats(updatedChats);
    setNewMsgSent(true);
  };

  // Handle Incoming read notifications
  const handleIncomingReadNotif = async (payload: any) => {
    let updatedChats = [...chats];
    const { messages, chatID, readerID } = payload.data as {
      chatID: string;
      readerID: string;
      messages: { timestamp: number; messageID: string }[];
    };
    messages.forEach((chat) => {
      updatedChats = updateMessageStatus({
        chatData: updatedChats,
        chatID,
        messageID: chat.messageID,
        status: "read",
        readerID,
      });
    });
    setChats(updatedChats);
    setNewMsgSent(!newMsgSent);
  };

  // Update the message status and return the updated chat.
  const updateMessageStatus = ({
    chatData,
    chatID,
    messageID,
    status,
    newMessageID,
    readerID,
  }: {
    chatData: Chat[];
    chatID: string;
    messageID: string;
    newMessageID?: string;
    readerID?: string;
    status: "sent" | "pending" | "read";
  }) => {
    // mark message as sent
    const chatIndex = chatData.findIndex((chat) => chat.id === chatID);

    if (chatIndex >= 0) {
      const msgIndex = chatData[chatIndex].messages.findIndex(
        (m) => m.id === messageID
      );
      if (msgIndex >= 0) {
        const currMessage = chatData[chatIndex].messages[msgIndex];
        chatData[chatIndex].messages[msgIndex] = {
          ...currMessage,
          status: status,
          readers: readerID
            ? [...currMessage.readers, readerID]
            : currMessage.readers,
          id: newMessageID ?? messageID,
        };
      }
    }
    return chatData;
  };

  const getShipperDetails = async () => {
    if (selectedChat?.participants) return;

    try {
      setCompanyLoading(LoadingState.Pending);
      const result = await fetchLoadDetails(selectedChat!.loadID);
      console.log("result", result);
      const index = chats.findIndex((c) => c.id === selectedChat?.id);
      if (index > -1) {
        chats[index].shipper = result.shipper;
        chats[index].participants = [{ ...result.shipper, role: "shipper" }];
        if (result.driver)
          chats[index].participants.push({ ...result.driver, role: "driver" });
        setCurrentChatLoad(result);
      }
      setCompanyLoading(LoadingState.Success);
    } catch (error) {
      console.error("error fetching shipper details", error);
      setCompanyLoading(LoadingState.Error);
      throw Error("Could not retrieve shipper info");
    }
  };

  const handleNewChatCreated = async (payload: any) => {
    const chat = [payload.data].map<Chat>((chat) => ({
      ...chat,
      id: chat.chatID,
      dropOffLocation: chat.dropOffLocation.name,
      pickupLocation: chat.pickupLocation.name,
      loadNumber: chat.loadNum,
      pickupAddress: chat.pickupLocation.address,
      dropOffAddress: chat.dropOffLocation.address,
      createdAt: new Date(chat.createdAt).toISOString(),
      messages: [],
    }))[0];
    setChats([chat, ...chats]);
  };

  const handleChatArhived = (payload: any) => {
    const chatID = payload.data.chatID;
    const updatedChats = [...chats];
    let deletedLoadNum;
    const index = updatedChats.findIndex((t) => t.id === chatID);
    if (index >= 0) {
      deletedLoadNum = updatedChats[index].loadNumber;
      updatedChats.splice(index, 1);
      setChats(updatedChats);
    }
    return deletedLoadNum;
  };

  const searchChatsAndMessages = async (term: string): Promise<void> => {
    return new Promise((resolve) => {
      if (!term.trim()) {
        setSearchedChats([]);
        setSearchedMessages([]);
        setIsSearching(false);
        setSearchTerm("");
        resolve();
        return;
      }

      setIsSearching(true);

      const terms = term.toLowerCase().split(/\s+/).filter(Boolean);

      // Filter chats
      const matchedChats = chats.filter((chat) =>
        terms.every(
          (t) =>
            chat.pickupLocation.toLowerCase().includes(t) ||
            chat.dropOffLocation.toLowerCase().includes(t) ||
            chat.loadNumber.toLowerCase().includes(t) ||
            chat.shipperName?.toLowerCase().includes(t)
        )
      );

      // Filter messages
      const matchedMessages: Message[] = [];
      chats.forEach((chat) => {
        chat.messages.forEach((msg) => {
          if (terms.every((t) => msg.content.toLowerCase().includes(t))) {
            matchedMessages.push({ ...msg, chatID: chat.id });
          }
        });
      });

      setSearchTerm(term);
      setSearchedChats(matchedChats);
      setSearchedMessages(matchedMessages);

      resolve();
    });
  };

  const clearSearch = () => {
    setSearchedChats([]);
    setSearchedMessages([]);
    setIsSearching(false);
    setSearchTerm("");
  };

  const values: ChatContextModel = {
    loadingState,
    chats,
    searchTerm,
    participantDrawer,
    inputMessage,
    newMsgSent,
    selectedChat,
    webSocket,
    companyID,
    companyLoading,
    tabNotificationCount,
    currentChatLoad,
    isSearching,
    searchedChats,
    searchedMessages,
    highlightedMessageId,
    clearSearch,
    setCompanyName,
    sendWebsocketAction: sendAction,
    setHighlightedMessageId,
    setSearchTerm,
    handleNewChatCreated,
    searchChatsAndMessages,
    handleChatArhived,
    getActiveChats,
    getShipperDetails,
    handleIncomingReadNotif,
    setTabNotificationCount,
    setCompanyID,
    setLoadingState,
    setParticipantDrawer,
    handleIncomingAllChats,
    handleIncomingNewChats,
    handleIncomingNewMessage,
    sendMessage,
    setChats,
    readMessages,
    setNewMsgSent,
    setInputMessage,
    setSelectedChat,
  };

  return (
    <ChatsContext.Provider value={values}>{children}</ChatsContext.Provider>
  );
};
